import { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import {
  AlertTriangle,
  BadgeCheck,
  Bot,
  CheckCheck,
  ChevronLeft,
  ChevronRight,
  Clock,
  FileCode,
  Globe,
  Mail,
  MessageSquare,
  Paperclip,
  Receipt,
  Send,
  X,
} from 'lucide-react';
import { cn, formatDateTime } from '@/lib/utils';
import type { VendorCommunication } from '@/lib/types';

// Communication trace for one outgoing PO, rendered for the supplier's channel.
export function VendorTrace({ channel, items }: { channel: string; items: VendorCommunication[] }) {
  if (items.length === 0) {
    return (
      <div className="border border-dashed border-line px-4 py-10 text-center text-sm text-muted">
        No communication with this supplier yet.
      </div>
    );
  }
  if (channel === 'sftp_edi') return <EdiTrace items={items} />;
  if (channel === 'website') return <WebTrace items={items} />;
  return <MessageThread items={items} />;
}

// ---------------------------------------------------------------- EDI ----

function ediIcon(c: VendorCommunication) {
  if (c.status === 'error') return AlertTriangle;
  if (c.status === 'pending') return Clock;
  const t = c.title.toLowerCase();
  if (t.includes('810') || t.includes('invoice')) return Receipt;
  if (t.includes('855')) return BadgeCheck;
  if (t.includes('997')) return CheckCheck;
  if (t.includes('sent')) return Send;
  return FileCode;
}

const STATUS_STYLES = {
  done: { box: 'border-line bg-surface-2', icon: 'bg-brand/15 text-brand' },
  pending: { box: 'border-dashed border-amber/50 bg-amber/5', icon: 'bg-amber/15 text-amber' },
  error: { box: 'border-danger/50 bg-danger/5', icon: 'bg-danger/15 text-danger' },
} as const;

function EdiTrace({ items }: { items: VendorCommunication[] }) {
  return (
    <ol className="relative space-y-3">
      {items.map((c, i) => {
        const Icon = ediIcon(c);
        const confirmed = c.status === 'done' && /855|810/.test(c.title);
        const style = STATUS_STYLES[c.status];
        return (
          <li key={c.id} className="relative">
            {i < items.length - 1 && (
              <span className="absolute left-[27px] top-full z-0 h-3 w-px bg-line" aria-hidden />
            )}
            <div className={cn('relative z-10 flex items-start gap-4 border p-4', style.box, confirmed && 'border-success/40 bg-success/5')}>
              <span
                className={cn(
                  'flex h-6 w-6 shrink-0 items-center justify-center rounded-full',
                  confirmed ? 'bg-success/15 text-success' : style.icon,
                )}
              >
                <Icon className="h-3.5 w-3.5" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                  <span className="font-head text-base font-extrabold uppercase tracking-wide text-ink">{c.title}</span>
                  <time className="text-xs text-faint">{formatDateTime(c.occurredAt)}</time>
                </div>
                {c.body && <p className="mt-1 break-words text-sm text-muted">{c.body}</p>}
                {c.attachmentName && (
                  <span className="mt-2 inline-flex items-center gap-1.5 border border-line bg-surface px-2 py-0.5 font-mono text-xs text-muted">
                    <FileCode className="h-3 w-3" />
                    {c.attachmentName}
                  </span>
                )}
              </div>
            </div>
          </li>
        );
      })}
    </ol>
  );
}

// ------------------------------------------------------ Email / SMS ----

function MessageThread({ items }: { items: VendorCommunication[] }) {
  return (
    <ol className="space-y-4">
      {items.map((c) => {
        const outbound = c.direction === 'outbound';
        const sms = c.medium === 'sms';
        const Icon = sms ? MessageSquare : Mail;
        return (
          <li key={c.id} className={cn('flex items-end gap-2', outbound ? 'justify-end' : 'justify-start')}>
            <div className={cn('w-full max-w-[80%] sm:max-w-[72%]')}>
              <div className={cn('mb-1 flex items-center gap-1.5 text-xs text-faint', outbound && 'justify-end')}>
                <Icon className="h-3 w-3" />
                <span className="truncate">
                  {outbound ? 'Lucille AI' : c.sender} → {outbound ? c.recipient : 'Lucille'}
                </span>
                <span>·</span>
                <time className="whitespace-nowrap">{formatDateTime(c.occurredAt)}</time>
              </div>
              <div
                className={cn(
                  'border px-4 py-3 text-sm',
                  outbound ? 'border-brand/30 bg-brand/10' : 'border-line bg-surface-3',
                  sms && 'rounded-2xl',
                  c.status === 'error' && 'border-danger/50 bg-danger/10',
                  c.status === 'pending' && 'border-dashed border-amber/50 bg-amber/5',
                )}
              >
                {!sms && <div className="mb-1.5 font-medium text-ink">{c.title}</div>}
                {c.body && <p className="whitespace-pre-line break-words text-ink/90">{c.body}</p>}
                {c.attachmentName && (
                  <span className="mt-2 inline-flex items-center gap-1.5 border border-line bg-surface px-2 py-0.5 text-xs text-muted">
                    <Paperclip className="h-3 w-3" />
                    {c.attachmentName}
                  </span>
                )}
              </div>
              {c.status !== 'done' && (
                <div className={cn('mt-1 text-xs', outbound && 'text-right', c.status === 'error' ? 'text-danger' : 'text-amber')}>
                  {c.status === 'error' ? 'Not delivered' : 'Scheduled — not sent yet'}
                </div>
              )}
            </div>
            {outbound && (
              <span
                className="mb-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-brand/40 bg-brand/15 text-brand"
                title="Sent by Lucille's AI ordering agent"
                aria-label="Sent by AI agent"
              >
                <Bot className="h-4 w-4" />
              </span>
            )}
          </li>
        );
      })}
    </ol>
  );
}

// ------------------------------------------------------------- Web ----

function WebTrace({ items }: { items: VendorCommunication[] }) {
  const [open, setOpen] = useState<number | null>(null);
  const shots = items.filter((c) => c.imageUrl);

  return (
    <>
      <ol className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {items.map((c, i) => {
          const shotIndex = shots.indexOf(c);
          return (
            <li
              key={c.id}
              className={cn(
                'flex flex-col overflow-hidden border bg-surface-2',
                STATUS_STYLES[c.status].box,
              )}
            >
              {c.imageUrl ? (
                <button
                  type="button"
                  onClick={() => setOpen(shotIndex)}
                  className="group relative block text-left"
                  aria-label={`Open screenshot: ${c.title}`}
                >
                  <BrowserFrame>
                    <img
                      src={c.imageUrl}
                      alt={c.title}
                      loading="lazy"
                      className="aspect-[16/10] w-full object-cover opacity-90 transition-opacity group-hover:opacity-100"
                    />
                  </BrowserFrame>
                </button>
              ) : (
                <div className="flex aspect-[16/10] items-center justify-center bg-surface-3 text-muted">
                  <Clock className="h-6 w-6" />
                </div>
              )}
              <div className="flex flex-1 items-start gap-3 p-3">
                <span
                  className={cn(
                    'flex h-6 w-6 shrink-0 items-center justify-center rounded-full font-mono text-xs',
                    STATUS_STYLES[c.status].icon,
                  )}
                >
                  {c.status === 'error' ? <AlertTriangle className="h-3.5 w-3.5" /> : i + 1}
                </span>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-ink">{c.title}</div>
                  {c.body && <p className="mt-0.5 text-xs text-muted">{c.body}</p>}
                  <time className="mt-1 block text-[11px] text-faint">{formatDateTime(c.occurredAt)}</time>
                </div>
              </div>
            </li>
          );
        })}
      </ol>

      <Dialog.Root open={open !== null} onOpenChange={(o) => !o && setOpen(null)}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-40 bg-black/85 backdrop-blur-sm" />
          {open !== null && shots[open] && (
            <Dialog.Content
              aria-describedby={undefined}
              className="fixed left-1/2 top-1/2 z-50 w-[calc(100vw-2rem)] max-w-5xl -translate-x-1/2 -translate-y-1/2 focus:outline-none"
            >
              <div className="mb-3 flex items-center justify-between gap-4">
                <Dialog.Title className="text-sm text-ink">
                  <span className="mr-2 font-mono text-brand">
                    {open + 1}/{shots.length}
                  </span>
                  {shots[open].title}
                </Dialog.Title>
                <Dialog.Close asChild>
                  <button className="rounded p-1.5 text-muted hover:bg-surface-3 hover:text-ink" aria-label="Close">
                    <X className="h-5 w-5" />
                  </button>
                </Dialog.Close>
              </div>
              <div className="relative">
                <BrowserFrame large>
                  <img src={shots[open].imageUrl!} alt={shots[open].title} className="max-h-[75vh] w-full object-contain" />
                </BrowserFrame>
                <LightboxNav side="left" disabled={open === 0} onClick={() => setOpen(open - 1)} />
                <LightboxNav side="right" disabled={open === shots.length - 1} onClick={() => setOpen(open + 1)} />
              </div>
              {shots[open].body && <p className="mt-3 text-sm text-muted">{shots[open].body}</p>}
            </Dialog.Content>
          )}
        </Dialog.Portal>
      </Dialog.Root>
    </>
  );
}

function BrowserFrame({ children, large }: { children: React.ReactNode; large?: boolean }) {
  return (
    <div className="border-b border-line bg-canvas">
      <div className={cn('flex items-center gap-1.5 border-b border-line bg-surface-3 px-2.5', large ? 'py-2' : 'py-1.5')}>
        <span className="h-2 w-2 rounded-full bg-danger/70" />
        <span className="h-2 w-2 rounded-full bg-amber/70" />
        <span className="h-2 w-2 rounded-full bg-success/70" />
        <span className="ml-2 flex min-w-0 flex-1 items-center gap-1 truncate bg-surface-2 px-2 py-0.5 text-[10px] text-faint">
          <Globe className="h-2.5 w-2.5 shrink-0" /> supplier portal
        </span>
      </div>
      {children}
    </div>
  );
}

function LightboxNav({ side, disabled, onClick }: { side: 'left' | 'right'; disabled: boolean; onClick: () => void }) {
  const Icon = side === 'left' ? ChevronLeft : ChevronRight;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={side === 'left' ? 'Previous screenshot' : 'Next screenshot'}
      className={cn(
        'absolute top-1/2 -translate-y-1/2 rounded-full bg-black/60 p-2 text-ink transition-opacity hover:bg-black/80 disabled:opacity-0',
        side === 'left' ? 'left-3' : 'right-3',
      )}
    >
      <Icon className="h-5 w-5" />
    </button>
  );
}
